import { Air, SsidChart, Thermostat } from '@mui/icons-material';
import { Grid, Divider, Typography, IconButton } from '@mui/material';
import { styled } from '@mui/material';
import { grey } from '@mui/material/colors';
import { addMinutes } from 'date-fns';
import WeatherStationsIcon from 'features/locations/WeatherStationsIcon';
import AnalyzerChip from 'features/weather/AnalyzerChip';
import {
  convertDBNumberNoDecimals,
  convertDBNumberNoDecimalsNoUnits,
  getUnitForMeasurementType,
} from 'features/weather/utils/dataConverters';
import { useImperialMeasurements } from 'features/weatherWalls/hooks/useImperialMeasurements';
import { useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import { Location } from 'types';
import ForecastDetails from './ForecastDetails';
import { useFormatForecastData } from './hooks/useFormatForecastData';
import { useAnalyzersByLocation } from 'features/locations/api/getAnalyzersByLocation';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';
import { formatInTimeZone } from 'date-fns-tz';

interface Props {
  location: Location;
  hide: boolean;
}

const StyledGrid = styled(Grid)(({ theme }) => ({
  paddingLeft: '1.2rem',
  paddingRight: '1.2rem',
  maxWidth: '100%',
  '& *': {
    marginBottom: '.1rem',
  },
}));

const ForecastContainer = ({ location, hide }: Props) => {
  const inImperial = useAppSelector(
    (state) => state.auth.user?.us_customary_units
  );
  const { timeFormat, dateFormat, monthAbbreviatedWordDayFormat, timezone } =
    useUserDateTime();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const weather = useImperialMeasurements(location.weather);
  const handleClose = () => {
    setDetailsOpen(false);
  };
  const { data: analyzersForLocation } = useAnalyzersByLocation({
    locationId: location.id,
  });
  const { formattedForecast: convertedForecast } = useFormatForecastData(
    location.id
  );

  if (hide) return null;
  if (!weather || weather.length === 0)
    return (
      <StyledGrid container item direction='column'>
        <Typography color={grey[500]} sx={{ pb: '.5rem', pt: '.5rem' }}>
          No weather data for {location.name} loaded.
        </Typography>
      </StyledGrid>
    );
  return (
    <StyledGrid container item direction='column'>
      <Grid item style={{ width: '100%', paddingBottom: '.5rem' }}>
        <Divider />
      </Grid>
      <Grid item container justifyContent='space-between' alignItems='center'>
        <Grid item xs container alignItems='center'>
          {location?.weather_station_id && (
            <WeatherStationsIcon
              stationId={location.weather_station_id}
              sx={{ mr: '.2rem' }}
            />
          )}
          <Typography>{location.name}</Typography>
          {convertedForecast &&
            convertedForecast?.dailyForecast.length > 0 &&
            convertedForecast?.hourlyForecast.length > 0 && (
              <IconButton
                onClick={() => setDetailsOpen(true)}
                aria-label='see-more'
                color='primary'
                sx={{ ml: '.2rem' }}
              >
                <SsidChart />
              </IconButton>
            )}
        </Grid>
        <Grid item container xs alignItems='center' wrap='nowrap'>
          <Grid xs item style={{ minWidth: '5rem' }}>
            <Typography variant='body2'>{weather[0].condition}</Typography>
          </Grid>
          <Grid
            xs
            item
            container
            alignItems='center'
            style={{ minWidth: '5rem' }}
          >
            <Thermostat />
            <Typography variant='body2'>
              {convertDBNumberNoDecimalsNoUnits(weather[0].temp_c)}&deg;
              {getUnitForMeasurementType('temp_c', inImperial)}
            </Typography>
          </Grid>
          <Grid
            xs
            item
            container
            alignItems='center'
            style={{ minWidth: '5rem' }}
          >
            <Air />
            <Typography sx={{ marginLeft: '.1rem' }} variant='body2'>
              {convertDBNumberNoDecimals(
                weather[0].wind_kph,
                getUnitForMeasurementType('wind_kph', inImperial)
              )}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      {!convertedForecast ||
      convertedForecast?.dailyForecast.length === 0 ||
      convertedForecast?.hourlyForecast.length === 0 ? (
        <StyledGrid container item direction='column'>
          <Typography color={grey[500]} sx={{ pb: '.5rem', pt: '.5rem' }}>
            No forecast data for {location.name} loaded.
          </Typography>
        </StyledGrid>
      ) : (
        <Grid item container>
          {convertedForecast?.dailyForecast.map((dailyData, index) => (
            <Grid item container xs direction='column' key={index}>
              <Grid item container justifyContent='center' alignItems='center'>
                <Typography>
                  {formatInTimeZone(
                    addMinutes(
                      new Date(dailyData.date),
                      new Date(dailyData.date).getTimezoneOffset()
                    ),
                    timezone,
                    monthAbbreviatedWordDayFormat
                  )}
                </Typography>
              </Grid>
              <Grid container item direction='column'>
                <Grid item>
                  <img
                    height='50px'
                    width='50px'
                    src={dailyData.icon ? dailyData.icon : ''}
                    alt='Forecast icon'
                  />
                </Grid>
                <Grid container item xs direction='column'>
                  <Grid container item xs>
                    <Typography
                      variant='body2'
                      sx={{ pl: '.2rem', pr: '.2rem' }}
                    >
                      {dailyData.maxtemp_c?.toFixed(0) === '-0'
                        ? '0'
                        : dailyData.maxtemp_c?.toFixed(0)}
                      &deg;
                    </Typography>
                    <Divider orientation='vertical' />
                    <Typography
                      variant='body2'
                      sx={{ pl: '.2rem', pr: '.2rem' }}
                      color={grey[500]}
                    >
                      {dailyData.mintemp_c?.toFixed(0) === '-0'
                        ? '0'
                        : dailyData.mintemp_c?.toFixed(0)}
                      &deg;
                    </Typography>
                  </Grid>
                  <Grid item container alignItems='center'>
                    <img
                      height='15'
                      src='/raindrops-svgrepo-com.svg'
                      alt='raindrops'
                      style={{
                        filter:
                          'invert(52%) sepia(79%) saturate(2325%) hue-rotate(189deg) brightness(99%) contrast(104%)',
                      }}
                    />
                    <Typography variant='body2' sx={{ pl: '.1rem' }}>
                      {dailyData.daily_chance_of_rain + '%'}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ))}
        </Grid>
      )}
      <Grid item container alignItems='center' style={{ marginLeft: '2rem' }}>
        {analyzersForLocation && analyzersForLocation.length > 0 && (
          <>
            <Typography variant='body2' sx={{ marginRight: '.5rem' }}>
              Analyzers:{' '}
            </Typography>
            <Grid item xs container wrap='wrap'>
              {analyzersForLocation.map((analyzer) => (
                <Grid item key={analyzer.id} style={{ marginRight: '.2rem' }}>
                  <AnalyzerChip name={analyzer.name} analyzerId={analyzer.id} />
                </Grid>
              ))}{' '}
            </Grid>
          </>
        )}
      </Grid>
      <Grid item container justifyContent='flex-end'>
        <Typography color={grey[500]} variant='body2'>
          {'Last updated: ' +
            formatInTimeZone(
              new Date(weather[0].time),
              timezone,
              `${dateFormat} ${timeFormat}`
            )}
        </Typography>
      </Grid>
      <ForecastDetails
        open={detailsOpen}
        handleClose={handleClose}
        forecast={convertedForecast}
        lastUpdated={formatInTimeZone(
          new Date(weather[0].time),
          timezone,
          `${dateFormat} ${timeFormat}`
        )}
      />
    </StyledGrid>
  );
};

export default ForecastContainer;
